<template>
  <div class="senderIdentityEditor">
    <div v-if="loading" class="hint">Loading...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <template v-else>
      <!-- Display name -->
      <div class="field">
        <label>Display name</label>
        <input class="input" v-model="draft.full_name" placeholder="e.g. Jane Doe" @blur="savePatch" />
        <div class="hint" style="margin:4px 0 0;">Controls the name shown in the "From" line of emails sent from this address.</div>
      </div>

      <!-- ── Signatures ─────────────────────────────────────── -->
      <div class="section">
        <div class="section-title">Signatures</div>

        <!-- Signature selection (which to use for initial vs reply) -->
        <div v-if="signatures.length > 0" class="sigSelectionRow">
          <div class="field" style="margin-bottom:0;">
            <label>Use for initial emails</label>
            <select class="input" v-model="draft.signature_initial_id" @change="savePatch" style="max-width:240px;">
              <option :value="null">Auto (first signature)</option>
              <option v-for="sig in signatures" :key="sig.id" :value="sig.id">{{ sig.name }}</option>
            </select>
          </div>
          <div class="field" style="margin-bottom:0;">
            <label>Use for replies</label>
            <select class="input" v-model="draft.signature_reply_id" @change="savePatch" style="max-width:240px;">
              <option :value="null">Auto (first signature)</option>
              <option v-for="sig in signatures" :key="sig.id" :value="sig.id">{{ sig.name }}</option>
            </select>
          </div>
        </div>

        <!-- Signatures table -->
        <div class="sigTableWrap">
          <table class="sigTable">
            <thead>
              <tr>
                <th>Name</th>
                <th>Last updated</th>
                <th style="width:1px;">
                  <button class="btn btn-sm" type="button" @click="createSignature" :disabled="signatures.length >= 10"><AppIcon name="plus" :size="12" /> New</button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="signatures.length === 0">
                <td colspan="3" class="hint" style="margin:0; text-align:center; padding:16px;">No signatures yet.</td>
              </tr>
              <tr
                v-for="sig in signatures"
                :key="sig.id"
                class="sigRow"
                :class="{ selected: activeSigId === sig.id }"
                @click="activeSigId = sig.id"
              >
                <td class="sigRowName">{{ sig.name }}</td>
                <td class="sigRowDate">{{ formatDate(sig.updated_at) }}</td>
                <td>
                  <button class="iconBtn danger" type="button" title="Delete" @click.stop="deleteSignature(sig)">
                    <AppIcon name="trash" :size="14" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Editor for selected signature -->
        <template v-if="activeSig">
          <div class="sigNameRow">
            <label style="font-size:12px; font-weight:700; color:var(--muted); white-space:nowrap;">Name</label>
            <input class="input" v-model="activeSig.name" placeholder="Signature name" @blur="saveSignature(activeSig)" style="flex:1;" />
          </div>

          <SignatureEditor
            :modelValue="activeSig.html"
            @update:modelValue="(val: string) => { activeSig!.html = val; debouncedSaveSignature(activeSig!) }"
          />

          <!-- CAN-SPAM hint — https://www.ftc.gov/sites/default/files/documents/cases/2007/11/canspam.pdf §5(A)(iii) -->
          <div class="canSpamHint">
            <strong>CAN-SPAM compliance protip:</strong> Commercial emails (if the
            recipient has not provided prior affirmative consent to receipt of the message)
            must include a valid physical postal address of the sender. We recommend you
            include this in your signature to ensure it is part of every email sent.
          </div>
        </template>
      </div>

      <!-- ── Unsubscribe footer ─────────────────────────────── -->
      <div class="section">
        <div class="section-title">Unsubscribe Footer</div>

        <!-- Preview of what the unsubscribe will look like -->
        <div class="unsubPreview">
          <div class="unsubPreviewLabel">Preview</div>
          <div class="unsubPreviewText" v-html="unsubPreviewHtml"></div>
        </div>

        <label class="checkboxRow">
          <input type="checkbox" v-model="customizeUnsub" />
          <span>Customize unsubscribe footer for this sender</span>
        </label>

        <template v-if="customizeUnsub">
          <div class="field">
            <label>Override template</label>
            <textarea
              class="textarea"
              rows="3"
              v-model="draft.unsubscribe_text_override"
              placeholder="e.g. Unsubscribe: {unsubscribe_url}"
              @blur="savePatch"
            />
            <div v-if="unsubOverrideError" class="hint" style="margin:4px 0 0; color:var(--danger);">{{ unsubOverrideError }}</div>
            <div v-else class="hint" style="margin:4px 0 0;">Must include <code>{unsubscribe_url}</code>. Replaces the workspace-level template for this sender only.</div>
          </div>
          <div class="field">
            <label>Link label</label>
            <input class="input" v-model="draft.unsubscribe_link_label" placeholder="Unsubscribe" maxlength="100" @blur="savePatch" style="max-width:300px;" />
            <div class="hint" style="margin:4px 0 0;">The clickable text in the unsubscribe link. Defaults to "Unsubscribe".</div>
          </div>
        </template>
      </div>

      <!-- ── Slack ────────────────────────────────────────── -->
      <div class="section">
        <div class="section-title">Slack</div>
        <div class="field">
          <label>Member ID <span class="hint" style="margin:0; display:inline;">(optional)</span></label>
          <input class="input" v-model="draft.slack_member_id" placeholder="U0123456789" @blur="savePatch" style="max-width:300px;" />
          <div class="hint" style="margin:4px 0 0;">When a prospect replies, Raven will @mention this person in Slack. Find your member ID in Slack under your profile → ⋯ → Copy member ID.</div>
        </div>
      </div>

      <!-- Save feedback (inline — parent can also read saveState via expose) -->
      <div v-if="saveError" class="error" style="margin-top:0;">{{ saveError }}</div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import AppIcon from '../../components/AppIcon.vue'
import SignatureEditor from './SignatureEditor.vue'
import { apiFetch } from '../../services/api'

interface Signature {
  id: string
  sender_email: string
  name: string
  html: string
  sort_order: number
  created_at: string
  updated_at: string
}

const props = defineProps<{
  senderEmail: string
  mode?: 'full' | 'personal'
  identity?: Record<string, any> | null
}>()

const mode = computed(() => props.mode || 'full')

const loading = ref(true)
const error = ref('')
const saveError = ref('')
const savedAt = ref('')

defineExpose({ savedAt, saving: loading })

const draft = ref({
  full_name: '',
  slack_member_id: '',
  signature_initial_id: null as string | null,
  signature_reply_id: null as string | null,
  unsubscribe_text_override: '',
  unsubscribe_link_label: '',
})

const signatures = ref<Signature[]>([])
const activeSigId = ref<string | null>(null)
const customizeUnsub = ref(false)
const workspaceUnsubText = ref('')
const workspaceUnsubLinkLabel = ref('')

const activeSig = computed(() => {
  if (!activeSigId.value) return signatures.value[0] || null
  return signatures.value.find(s => s.id === activeSigId.value) || signatures.value[0] || null
})

const unsubOverrideError = computed(() => {
  const t = (draft.value.unsubscribe_text_override || '').trim()
  if (t && !t.includes('{unsubscribe_url}')) return 'Must include {unsubscribe_url}.'
  return ''
})

const LINK_PLACEHOLDER = '___UNSUB_LINK___'

const unsubPreviewHtml = computed(() => {
  let tmpl: string
  let label: string
  if (customizeUnsub.value && draft.value.unsubscribe_text_override?.trim()) {
    tmpl = draft.value.unsubscribe_text_override.trim()
    label = draft.value.unsubscribe_link_label?.trim() || 'Unsubscribe'
  } else {
    tmpl = workspaceUnsubText.value || 'Unsubscribe: {unsubscribe_url}'
    label = workspaceUnsubLinkLabel.value || 'Unsubscribe'
  }
  // Replace template vars with a placeholder, then render the link styled
  const linkHtml = `<span class="unsubPreviewLink">${label}</span>`
  return tmpl
    .replace('{unsubscribe_link_text}', label)
    .replace('{unsubscribe_url}', linkHtml)
    .replace('{unsubscribe_link}', linkHtml)
})

// When the user checks "customize", pre-fill with the workspace default
// so they don't have to retype everything from scratch.
watch(customizeUnsub, (on) => {
  if (on && !draft.value.unsubscribe_text_override?.trim()) {
    draft.value.unsubscribe_text_override = workspaceUnsubText.value || 'Unsubscribe: {unsubscribe_url}'
  }
})

let saveTimeout: ReturnType<typeof setTimeout> | null = null

function debouncedSaveSignature(sig: Signature) {
  if (saveTimeout) clearTimeout(saveTimeout)
  saveTimeout = setTimeout(() => saveSignature(sig), 800)
}

onMounted(async () => {
  await loadData()
})

watch(() => props.senderEmail, () => {
  loadData()
})

async function loadData() {
  loading.value = true
  error.value = ''
  try {
    const email = props.senderEmail.trim().toLowerCase()

    let sigs: any[]
    try {
      sigs = await apiFetch(`/signatures?sender_email=${encodeURIComponent(email)}`)
    } catch (e: any) {
      throw new Error(`Failed to load signatures: ${e?.message || e}`)
    }
    signatures.value = Array.isArray(sigs) ? sigs : []
    if (signatures.value.length > 0) {
      activeSigId.value = signatures.value[0].id
    }

    // Load sender identity fields — use prop if provided, otherwise fetch
    let identity: Record<string, any> | undefined
    if (props.identity) {
      identity = props.identity
    } else {
      let settings: any
      try {
        settings = await apiFetch('/settings/email')
      } catch (e: any) {
        throw new Error(`Failed to load email settings: ${e?.message || e}`)
      }
      const identities = settings?.sender_identities || []
      identity = identities.find((i: any) => (i.email || '').trim().toLowerCase() === email)
      workspaceUnsubText.value = settings?.unsubscribe_text || ''
      workspaceUnsubLinkLabel.value = settings?.unsubscribe_link_label || ''
    }

    if (identity) {
      draft.value.full_name = identity.full_name || ''
      draft.value.slack_member_id = identity.slack_member_id || ''
      draft.value.signature_initial_id = identity.signature_initial_id || null
      draft.value.signature_reply_id = identity.signature_reply_id || null
      draft.value.unsubscribe_text_override = identity.unsubscribe_text_override || ''
      draft.value.unsubscribe_link_label = identity.unsubscribe_link_label || ''
      customizeUnsub.value = Boolean(identity.unsubscribe_text_override || identity.unsubscribe_link_label)
    }

    // If identity prop was provided, still fetch workspace unsub settings for the preview
    if (props.identity && !workspaceUnsubText.value) {
      try {
        const settings = await apiFetch('/settings/email')
        workspaceUnsubText.value = settings?.unsubscribe_text || ''
        workspaceUnsubLinkLabel.value = settings?.unsubscribe_link_label || ''
      } catch {
        // Non-critical — preview just uses defaults
      }
    }
  } catch (e: any) {
    error.value = e?.message || 'Failed to load'
  } finally {
    loading.value = false
  }
}

async function createSignature() {
  saveError.value = ''
  const name = `Signature ${signatures.value.length + 1}`
  try {
    const sig = await apiFetch('/signatures', {
      method: 'POST',
      body: { sender_email: props.senderEmail.trim().toLowerCase(), name, html: '' },
    })
    signatures.value.push(sig)
    activeSigId.value = sig.id
  } catch (e: any) {
    saveError.value = e?.message || 'Failed to create signature'
  }
}

async function saveSignature(sig: Signature) {
  saveError.value = ''
  try {
    const updated = await apiFetch(`/signatures/${sig.id}`, {
      method: 'PUT',
      body: { name: sig.name, html: sig.html },
    })
    const idx = signatures.value.findIndex(s => s.id === sig.id)
    if (idx >= 0) {
      signatures.value[idx] = updated
    }
    showSaved()
  } catch (e: any) {
    saveError.value = e?.message || 'Failed to save signature'
  }
}

async function deleteSignature(sig: Signature) {
  saveError.value = ''
  try {
    await apiFetch(`/signatures/${sig.id}`, { method: 'DELETE' })
    signatures.value = signatures.value.filter(s => s.id !== sig.id)
    if (activeSigId.value === sig.id) {
      activeSigId.value = signatures.value[0]?.id || null
    }
    if (draft.value.signature_initial_id === sig.id) {
      draft.value.signature_initial_id = null
      savePatch()
    }
    if (draft.value.signature_reply_id === sig.id) {
      draft.value.signature_reply_id = null
      savePatch()
    }
  } catch (e: any) {
    saveError.value = e?.message || 'Failed to delete signature'
  }
}

async function savePatch() {
  saveError.value = ''
  try {
    await apiFetch(`/signatures/sender-identity/${encodeURIComponent(props.senderEmail.trim().toLowerCase())}`, {
      method: 'PATCH',
      body: {
        full_name: draft.value.full_name.trim() || null,
        slack_member_id: draft.value.slack_member_id.trim() || null,
        signature_initial_id: draft.value.signature_initial_id || '',
        signature_reply_id: draft.value.signature_reply_id || '',
        unsubscribe_text_override: customizeUnsub.value ? (draft.value.unsubscribe_text_override.trim() || '') : '',
        unsubscribe_link_label: customizeUnsub.value ? (draft.value.unsubscribe_link_label.trim() || '') : '',
      },
    })
    showSaved()
  } catch (e: any) {
    saveError.value = e?.message || 'Failed to save'
  }
}

let savedTimer: ReturnType<typeof setTimeout> | null = null
function formatDate(iso: string): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return ''
  }
}

function showSaved() {
  savedAt.value = 'just now'
  if (savedTimer) clearTimeout(savedTimer)
  savedTimer = setTimeout(() => { savedAt.value = '' }, 2000)
}
</script>

<style scoped>
.senderIdentityEditor {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ── Reuse host page conventions ─────────── */
.section {
  border-top: 1px solid var(--border);
  padding-top: 14px;
  margin-top: 14px;
}
.section-title {
  font-size: 11px;
  font-weight: 800;
  color: var(--table-header-text);
  letter-spacing: 0.06em;
  margin-bottom: 10px;
  text-transform: uppercase;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}
.field label {
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
}
.hint {
  color: var(--muted);
  font-size: 12px;
  margin: 10px 0 12px;
  line-height: 1.4;
}
.hint code {
  font-family: var(--font-mono, monospace);
  font-size: 11px;
  background: var(--bg-subtle, #f0f0f0);
  padding: 1px 4px;
  border-radius: 3px;
}
.error {
  color: var(--danger);
  font-size: 13px;
  margin-bottom: 10px;
}
.input {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 14px;
  line-height: 1.25;
  font-family: var(--font-sans, sans-serif);
  background: var(--bg, #fff);
  color: var(--text);
}
.textarea {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
  font-size: 14px;
  line-height: 1.35;
  font-family: var(--font-sans, sans-serif);
  background: var(--bg, #fff);
  color: var(--text);
}
.autoSaveIndicator.saved {
  font-size: 13px;
  font-weight: 600;
  color: var(--success, #059669);
  text-align: right;
}

/* ── Signature selection ──────────────────── */
.sigSelectionRow {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

/* ── Signatures table ────────────────────── */
.sigTableWrap {
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 10px;
}
.sigTable {
  width: 100%;
  border-collapse: collapse;
}
.sigTable th,
.sigTable td {
  padding: 8px 10px;
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
}
.sigTable th {
  text-align: left;
  font-size: 12px;
  font-weight: 800;
  color: var(--table-header-text);
  background: var(--table-header-bg);
}
.sigTable tbody tr:last-child td {
  border-bottom: none;
}
.sigRow {
  cursor: pointer;
  transition: background var(--duration-fast, 120ms) ease;
}
.sigRow:hover { background: var(--color-bg-subtle, var(--bg-hover, #f8f8f8)); }
.sigRow.selected { background: var(--color-bg-sunk, var(--bg-subtle, #ede9e1)); }
.sigRowName {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary, var(--text));
}
.sigRowDate {
  font-size: 12px;
  color: var(--color-text-tertiary, var(--muted));
}
.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  color: var(--muted);
  transition: color 120ms ease, background 120ms ease;
}
.iconBtn:hover { color: var(--text); background: var(--bg-hover, #f0f0f0); }
.iconBtn.danger:hover { color: var(--danger, #dc3545); }

.sigNameRow {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

/* ── CAN-SPAM ────────────────────────────── */
.canSpamHint {
  background: hsl(45, 100%, 95%);
  border: 1px solid hsl(45, 80%, 70%);
  border-radius: var(--radius-md, 8px);
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.5;
  color: hsl(30, 60%, 30%);
  margin-top: 10px;
}

/* ── Unsubscribe preview ─────────────────── */
.unsubPreview {
  border: 1px solid var(--border);
  border-radius: var(--radius-sm, 6px);
  padding: 10px 12px;
  background: var(--color-bg-subtle, var(--bg-subtle, #f8f8f8));
  margin-bottom: 10px;
}
.unsubPreviewLabel {
  font-size: 10px;
  font-weight: 700;
  color: var(--table-header-text, var(--muted));
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 4px;
}
.unsubPreviewText {
  font-size: 13px;
  color: var(--color-text-primary, var(--text));
  line-height: 1.5;
}
.unsubPreviewText :deep(.unsubPreviewLink) {
  color: #2563eb;
  text-decoration: underline;
}

.checkboxRow {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-primary, var(--text));
  cursor: pointer;
  margin-bottom: 12px;
}
</style>
